// ============================================================================
//  Constantes de control — equivalentes a controlador/app/config.py del
//  piloto Python. Los umbrales de gas y los escalones de Hz DEBEN validarse
//  con Seguridad antes de operar (ver SPEC.md).
// ============================================================================
import type {
  AmbientKey,
  ControlTiming,
  FanConfig,
  FanScale,
  GasKey,
  GasThreshold,
  ScannerZone,
  SimulationEngineConfig,
} from '../shared/types';

// --- Gases: umbrales (VALIDAR CON SEGURIDAD / DS 024-2016-EM) ---------------
export const GAS_THRESHOLDS: Record<GasKey, GasThreshold> = {
  co: { atencion: 25, peligro: 50, direction: 'high', unit: 'ppm' },
  co2: { atencion: 0.5, peligro: 1.0, direction: 'high', unit: '%vol' },
  o2: { atencion: 19.5, peligro: 18.0, direction: 'low', unit: '%vol' },
  no2: { atencion: 3, peligro: 5, direction: 'high', unit: 'ppm' },
};

/** Unidad y nombre en español de los parámetros ambientales de un sensor de
 *  gas (ver shared/types::AmbientKey). */
export const AMBIENT_UNITS: Record<AmbientKey, string> = { temperature: '°C', humidity: '%' };
export const AMBIENT_LABEL: Record<AmbientKey, string> = {
  temperature: 'temperatura',
  humidity: 'humedad',
};
/** Cómo se nombra en un mensaje la variable de un tópico de sensor de gas:
 *  `gas co`, o `temperatura del sensor de gas`. */
export function sensorVariableLabel(variable: string): string {
  return variable in AMBIENT_LABEL
    ? `${AMBIENT_LABEL[variable as AmbientKey]} del sensor de gas`
    : `gas ${variable}`;
}

/** Último segmento del tópico → parámetro ambiental, para el descubrimiento.
 *  Solo cuenta dentro de un grupo que también publica gases: un `temperatura`
 *  en el grupo de un ventilador sigue siendo un parámetro extra del variador. */
export const SENSOR_AMBIENT_SEGMENTS: Record<string, AmbientKey> = {
  temperatura: 'temperature',
  temperature: 'temperature',
  humedad: 'humidity',
  humidity: 'humidity',
};

// --- Ventilador: niveles por defecto -> frecuencia (Hz) ----------------------
// Ascendentes estrictos: el fail-safe usa el último (máximo).
export const DEFAULT_FAN_LEVELS_HZ = [40, 45, 50, 55, 60];
export const DEFAULT_FAN_LEVEL_NAMES = ['mínimo', 'bajo', 'medio', 'alto', 'máximo'];
// escala global por defecto (settings['fanScale']) — instalación sin
// ventiladores previos de los que heredarla, o registro guardado inválido.
export const DEFAULT_FAN_SCALE: FanScale = {
  levels: DEFAULT_FAN_LEVELS_HZ,
  names: DEFAULT_FAN_LEVEL_NAMES,
  unit: 'Hz',
  attentionLevel: null,
  dangerLevel: null,
};

// republicar la consigna aunque no cambie, como máximo cada minuto (por si el
// puente o el VFD se reiniciaron). Un cambio de valor se publica al instante:
// esto solo acota el reenvío de lo MISMO (ver control.service.ts::tick).
export const FAN_HEARTBEAT_SECONDS = 60;

// el color del LED de semáforo se reenvía aunque no cambie cada 10 s: el LED
// no guarda el color si se reinicia, y lo que reporta es lo último recibido.
export const STATUS_LIGHT_REPUBLISH_SECONDS = 10;

// --- Control del ventilador por defecto (editable desde /config) ------------
// Única fuente de los defaults: la semilla de settings, el arranque de
// ControlService y los fallbacks de FanController salen de aquí.
export const DEFAULT_CONTROL_TIMING: ControlTiming = {
  confirmUpSeconds: 15,
  dwellSeconds: 180,
  purgeSeconds: 600,
  // aparte de dwellSeconds a propósito: dwell decide BAJAR DE NIVEL con la
  // labor todavía ocupada; esto decide CORTAR el ventilador cuando ya no queda
  // nadie (ni acercándose). Son decisiones distintas y se ajustan por separado.
  offDelaySeconds: 900,
  // ocupación -> nivel: score = personas + 3×vehículos; >=1 ->1, >=5 ->2, >=10 ->3
  occupancyThresholds: [1, 5, 10],
  vehicleWeight: 3,
  // sin lecturas frescas de gas en este tiempo => fail-safe (ventilación máxima)
  gasStaleSeconds: 30,
  // sensor de gas que controla callado => fail-safe (desactivable en puesta en
  // marcha, 2026-10-20: los sensores recién instalados se cortan a cada rato y
  // no hay nadie dentro). El gas MEDIDO sigue subiendo el ventilador igual.
  gasFailsafeEnabled: true,
  // escáner/lector con zona asignada caído => fail-safe (desactivable en puesta
  // en marcha). Caído = sin reportar 'presente' este tiempo. Va aparte de
  // READER_OFFLINE_SECONDS, que sigue rigiendo el vencido de VFD/LED/estado.
  readerFailsafeEnabled: true,
  readerFailsafeSeconds: 90,
  // sin volver a leerse este tiempo con una dirección ('in'/'out') pendiente,
  // se confirma el ingreso/salida igual (no llegó a leer el escáner terminal).
  // DEBE ser menor que presenceForgetSeconds o esta rama nunca corre — es el
  // defecto latente del proyecto de referencia (trafic-manager-server), donde
  // el TTL de limpieza coincide exactamente con la ventana de retorno.
  // updateControl() lo hace cumplir.
  directionCommitSeconds: 60,
  // baliza FUERA y sin dirección pendiente sin verse este tiempo => se olvida
  // de memoria. Quien está DENTRO nunca se borra por tiempo (ver
  // presence.service.ts, "el silencio nunca es salida").
  presenceForgetSeconds: 300,
  // una lectura de un lector se descarta si otro lector de la misma bocamina
  // vio la misma baliza hace ≤12 s con al menos estos dB más (ver
  // modules/presence/rssi-arbiter.ts). 6 dB: en campo (Bocamina Sur,
  // 2026-10-16) la salida falsa vino de un exterior a −95 con el medio a −88.
  // 0 = sin arbitraje (comportamiento anterior).
  rssiMarginDb: 6,
  // modelo de control: 'occupancy' (el original) por defecto, para que
  // desplegar no cambie el comportamiento. 'fixed' (2026-10-19, pedido del
  // cliente para la fase de implementación): 45 Hz siempre, 55 Hz con gas.
  strategy: 'occupancy',
  fixedBaseHz: 45,
  fixedGasHz: 55,
  // Hz propios por ventilador (2026-10-21): apagado por defecto = el par global
  // rige a todos, como antes.
  fixedPerFan: false,
  fixedFanHz: [],
};

// Rango permitido para las dos frecuencias del modelo fijo (pedido del
// cliente, 2026-10-19): el operador las escribe libres dentro de este rango.
export const FIXED_MODEL_MIN_HZ = 40;
export const FIXED_MODEL_MAX_HZ = 60;

// --- Turnos por defecto (editable desde /config), ver ShiftsSettings --------
export const DEFAULT_SHIFTS = {
  day: { hours: 7, minutes: 0, voladuraMinutes: 0 },
  night: { hours: 19, minutes: 0, voladuraMinutes: 0 },
} as const;

// --- Alerta de voladura (ver jobs/shift-alert-job.ts) -----------------------
export const SHIFT_ALERT_PERIOD_MINUTES = 5;

// --- Voladura DETECTADA por el CO (ver modules/gas/blast-tracker.ts) --------
// Distinta de la ventana de arriba: esta es el estallido medido. Calibrado con
// prod (Bocamina Sur, 30 días a 22/10): una voladura lleva el frente de < 25 a
// ≥ 200 ppm en 15–25 s y a 600–2000 ppm; fuera de voladura el CO nunca pasó de
// 25 ppm (las horas con 25–176 son colas de voladura). 200 deja 3× de margen a
// la voladura más chica del mes (628 ppm) y 8× a lo normal.
export const BLAST_ONSET_PPM = 200;
// Firma de una voladura, además del umbral: SUBIDA RÁPIDA (de BLAST_CLEAR_PPM a
// BLAST_ONSET_PPM en como mucho esto; las reales, 15–25 s) — una subida lenta,
// como el humo de un equipo diésel, no es voladura —
export const BLAST_RISE_MAX_SECONDS = 60;
// — y SOSTENIDA (≥ BLAST_ONSET_PPM al menos esto de corrido; las reales se
// quedan 5–8 min) — un pico de una lectura de un sensor fallando tampoco.
export const BLAST_SUSTAIN_SECONDS = 10;
// llegada a otro sensor y despeje: el umbral de atención del CO
export const BLAST_CLEAR_PPM = GAS_THRESHOLDS.co.atencion;
// tope de seguridad: una voladura real se despeja en ~40 min; un sensor que se
// calla a mitad no la deja abierta para siempre.
export const BLAST_MAX_OPEN_SECONDS = 3 * 3600;
// Grabación de la escena del Home durante la voladura (modules/gas/blast-recorder.ts):
// arranca este margen ANTES del inicio (el Home repite desde el inicio y la
// detección llega hasta ~75 s después) y sigue este margen después del despeje.
export const BLAST_RECORD_MARGIN_SECONDS = 60;
// lo que el grabador recuerda sin voladura, para tener el margen de antes
export const BLAST_RECORD_PREROLL_SECONDS = 180;
// cada cuánto graba un cuadro COMPLETO (los demás traen solo lo que cambió)
export const BLAST_RECORD_KEYFRAME_SECONDS = 300;
// redondeo de `lastSeenAt` de la gente grabada (solo avisa de balizas calladas
// hace horas): exacto, la lista cambiaría en cada casillero
export const BLAST_RECORD_SEEN_QUANTUM_SECONDS = 600;

// --- Historial de alertas (ver modules/alerts/alert-tracker.ts) --------------
// Una alerta que vuelve antes de esto es el MISMO episodio: un lector que
// parpadea no deja cientos de filas.
export const ALERT_MERGE_SECONDS = 60;
// Al arrancar, sensores, lectores y variadores figuran desconectados hasta su
// primer mensaje: un «sin datos» de este rato es el arranque. No más que
// ALERT_MERGE_SECONDS: si no, un «sin datos» que seguía antes del reinicio se
// cerraría durante la gracia y volvería a abrirse como otro.
export const ALERT_BOOT_GRACE_SECONDS = 60;

// --- Avisos por WhatsApp (ver modules/whatsapp/whatsapp-notifier.ts) ---------
// Todo (desconexiones y ventilador parado) espera WA_EQUIPMENT_OFFLINE_MINUTES
// (env, 60 por defecto): los cortes rutinarios duran menos que eso.
// cada cuánto se juntan las vencidas en UN mensaje (y se reintenta si falló)
export const NOTIFY_CHECK_SECONDS = 60;
// Escalera de recordatorios mientras siga activo (igual que wapsi-manager-server):
// 1 h, 3 h, 6 h, 12 h y luego uno cada 24 h. Lo urgente es enterarse pronto,
// no que te lo repitan: un equipo caído dos días deja 6 mensajes, no 48.
export const NOTIFY_STEPS_HOURS = [1, 3, 6, 12];
export const NOTIFY_REPEAT_EVERY_HOURS = 24;
// Separación mínima entre dos mensajes cuando todo lo vencido son
// recordatorios (una caída nueva nunca espera).
export const NOTIFY_MIN_REMINDER_GAP_SECONDS = 30 * 60;

// Cada cuánto se revisa la configuración de tópicos en busca de errores
// humanos tolerados (repetidos / fuera de la raíz) y se emite el socket
// 'configWarnings' — ver jobs/config-warnings-job.ts. Una hora: es una
// condición ESTABLE, no un evento; revisarla más seguido no aportaría nada y
// el front igual recibe el último aviso al conectarse.
export const CONFIG_WARNINGS_PERIOD_MINUTES = 60;

// --- Lazo de control / dispositivos ------------------------------------------
export const CONTROL_PERIOD_SECONDS = 1;

// --- Rastro de funcionamiento por ventilador (history_samples) --------------
// Serie regular alineada al reloj — ver control-loop.ts::sampleFans(). Se
// escribe UNA fila por ventilador cada vez que el tick cruza a un bucket de
// este tamaño (bucket = floor(ts / HISTORY_SAMPLE_PERIOD_SECONDS) * …), con
// `ts` fijado al BORDE del bucket, no al instante real del tick. Así cada
// muestra representa exactamente este intervalo — sin eso, el cálculo de
// consumo/ahorro (integrar `power` contra `energy_baselines`) tendría que
// calcular el ancho de cada escalón fila por fila en vez de sumarlas.
// Reemplaza a la regla vieja "por cambio de nivel/encendido" (FAN_SAMPLE_MIN_
// SECONDS / FAN_SAMPLE_HEARTBEAT_SECONDS): los cambios entre muestras ya no
// generan fila — quedan igual en `events`.
export const HISTORY_SAMPLE_PERIOD_SECONDS = 300;
// export const HISTORY_SAMPLE_PERIOD_SECONDS = 60;
// Al arrancar el proceso, sampleFans() no escribe durante este tiempo
// (2026-10-15): el estado recién nacido no es dato —nivel 0 "arranque", sin
// telemetría del variador hasta que conecta MQTT, sin fail-safe hasta que vence
// el gas—. Un bucket que empieza en este lapso se escribe al terminarlo (con el
// `ts` de su borde); el del arranque no se escribe nunca (ver sampleFans()).
// Cubre gasStaleSeconds (30) + confirmUpSeconds (15) con margen.
export const HISTORY_SAMPLE_WARMUP_SECONDS = 60;

// --- Resumen de consumo/ahorro por turno (ver jobs/shift-summary-job.ts,
// modules/energy/shift-summary.service.ts, specs/20260817-04-…) -------------
// margen tras el cierre del turno antes de calcularlo: le da tiempo a
// control-loop.ts::sampleFans() de escribir la última muestra del bucket que
// cae justo en el borde, para no perderla por una carrera.
export const SHIFT_SUMMARY_GRACE_SECONDS = 90;
// tope de filas leídas de history_samples por ventilador por turno — un
// turno de 12 h a la cadencia mínima (60 s) son 720 filas; de sobra incluso
// si se reduce el período de muestreo más adelante.
export const HISTORY_MAX_ROWS_PER_SHIFT = 20000;
// ancho máximo que se le atribuye a UNA muestra al integrar kWh — el
// mecanismo de "no inventar huecos": una brecha más larga que esto entre dos
// filas (proceso caído, SD llena) no se cobra como consumo real ni como
// línea base (ver ShiftSummaryService.summarizeShift()).
export const SHIFT_SUMMARY_MAX_SAMPLE_WIDTH_SECONDS = 2 * HISTORY_SAMPLE_PERIOD_SECONDS;

// --- Línea base AUTOMÁTICA (ver modules/energy/auto-baseline.ts) -----------
// muestras de 5 min seguidas o no, al mismo Hz, que hacen falta para dar por
// medido un punto (máximo con gas o normal): 3 = 15 min — un pico de gas de
// 10 min no alcanza para cambiar la línea base.
export const AUTO_BASELINE_MIN_SAMPLES = 3;
// |Hz medido − Hz ordenado| máximo para que una muestra cuente como régimen
// estable: descarta rampas y el variador que no obedece (Norte midió 50 Hz con
// 45 ordenados el 19-21/10, P220 en Remoto 2).
export const AUTO_BASELINE_HZ_TOLERANCE = 0.5;
// al arrancar sin ninguna medición automática, cuántos días de turnos ya
// cerrados se miden de una (history_samples guarda 365, pero la potencia
// medida existe recién desde 2026-10-14).
export const AUTO_BASELINE_SEED_DAYS = 14;

// --- Ahorro del turno EN CURSO (ver jobs/shift-progress-job.ts,
// ShiftSummaryService.progress()) -------------------------------------------
// cada cuánto se recalcula y empuja por Socket.IO ('energy'). El dato solo
// cambia cuando entra un bucket nuevo de HISTORY_SAMPLE_PERIOD_SECONDS
// (5 min) — 60 s es de sobra para que el front lo vea "al toque" sin
// recalcular la integral del turno en cada tick de 1 s del lazo de control.
export const ENERGY_PROGRESS_PERIOD_SECONDS = 60;
// tope de puntos de la CURVA de consumo que viaja dentro de ese mismo evento
// (ver modules/energy/shift-curve.ts). Con HISTORY_SAMPLE_PERIOD_SECONDS = 300 un
// turno de 12 h da 144 puntos y este tope no se alcanza nunca; existe por si el
// período de muestreo vuelve a 60 s (144 -> 720) y para que el payload que se
// difunde a TODAS las pantallas no pueda crecer sin techo.
export const ENERGY_CURVE_MAX_POINTS = 720;

// sin reportar 'presente' este tiempo => lector offline. El fail-safe por área
// usa su propio valor editable (DEFAULT_CONTROL_TIMING.readerFailsafeSeconds).
export const READER_OFFLINE_SECONDS = 90;

// --- Zona horaria del operador (Perú, UTC-5, sin horario de verano) --------
// Todo lo demás se guarda/expone SIEMPRE en UTC (`history_samples.ts` epoch,
// `created_at` ISO con 'Z') y la conversión a la zona del operador es
// responsabilidad del FRONTEND. `history_samples.date` es la EXCEPCIÓN
// deliberada: un valor "solo fecha" (sin hora) no puede llevar zona horaria
// consigo, así que el corte de día hay que decidirlo UNA vez, al escribir —
// si se guardara con el día calendario UTC, un evento de las 22:00 en Perú
// (03:00 UTC del día siguiente) quedaría fechado un día adelantado y ningún
// frontend podría deshacer eso después (el string ya perdió la hora). Por
// eso `date` se calcula restando este offset antes de tomar el día (ver
// history.repository.ts::localDate() / database.ts::backfillHistoryTimestamps()).
export const OPERATOR_UTC_OFFSET_HOURS = -5;

// --- Retención en BD (purga periódica, ver jobs/retention-job.ts) -----------
// 365 días (era 30): con la serie regular de 5 min (~288 filas/día/ventilador)
// un año de historia pesa ~105k filas/ventilador, manejable en SQLite, y es
// lo que necesita el análisis de ahorro energético.
export const HISTORY_RETENTION_DAYS = 365;
export const EVENTS_RETENTION_DAYS = 365; // NOTA: aún sin job de purga, ver retention-job.ts
// registro de ingresos/salidas (`access_movements`). A un par de cientos de
// filas por día pesa mucho menos que history_samples, pero se purga igual: es
// la SD de la Pi, y una tabla sin purga ya creció sin límite una vez acá.
export const ACCESS_RETENTION_DAYS = 365;

// tope de rango para GET /api/history/samples — más que HISTORY_RETENTION_DAYS
// a propósito, para que nunca recorte algo que todavía exista en la tabla.
export const HISTORY_MAX_RANGE_DAYS = 31;

// --- Histórico de gases (ver modules/gas-history) ---------------------------
// Dos niveles, mismo modelo que readings → readingshourlies de WAPSI:
//   - `gas_samples` (crudo): una fila por bocamina por minuto con una muestra
//     cada GAS_SAMPLE_SLOT_SECONDS dentro. Se guarda GAS_RAW_RETENTION_DAYS.
//   - `gas_hourly` (resumen): una fila por bocamina por hora con mín/máx/prom
//     y 12 muestras de 5 min. Se guarda HISTORY_RETENTION_DAYS. Es lo que leen
//     las vistas de semana y mes: medido con un año de datos, el mes pasa de
//     44k filas / 8 MB / 24 MB leídos de la SD a 744 filas y ~1 ms.
export const GAS_SAMPLE_SLOT_SECONDS = 5;
export const GAS_SAMPLE_SLOTS = 60 / GAS_SAMPLE_SLOT_SECONDS;
export const GAS_RAW_RETENTION_DAYS = 30;
// Muestras de 5 min dentro de cada hora del resumen.
export const GAS_HOURLY_BLOCK_SECONDS = 300;
export const GAS_HOURLY_BLOCKS = 3600 / GAS_HOURLY_BLOCK_SECONDS;
// El minuto 59 de una hora se escribe recién cuando un tick cae en la hora
// siguiente: se espera este margen antes de dar la hora por cerrada.
export const GAS_ROLLUP_GRACE_SECONDS = 120;
// El relleno sin red acepta gas hasta acá: recalcular una hora cuyo crudo ya
// se purgó pisaría su resumen completo con uno parcial. Un día de margen
// contra la purga, que corta por horas enteras.
export const GAS_BACKFILL_MAX_AGE_DAYS = GAS_RAW_RETENTION_DAYS - 1;
// tope de rango de GET /api/gas-history?bucket=raw: un turno de 12 h son 8640
// puntos; 48 h cubre un turno con margen sin mandar un mes a 5 s (~535k puntos).
export const GAS_RAW_MAX_RANGE_HOURS = 48;

// --- Relleno de histórico (POST /api/history/backfill) ----------------------
// Topes de UNA tanda de relleno, para que un equipo que estuvo días sin red no
// mande su buffer entero en un request. 20000 puntos son ~69 días de una
// variable muestreada cada 5 min, o ~14 días muestreada cada minuto: de sobra
// para cualquier corte real, y a ~25 bytes por punto entra cómodo en el límite
// de 2mb de express.json(). Pasarse no es un error del operador sino del
// firmware, así que se responde 400 pidiendo partir el envío en tandas — nunca
// se recorta en silencio, que perdería dato sin que nadie se entere.
export const MAX_BACKFILL_POINTS = 20000;
// Un equipo publica una decena de magnitudes como mucho; 100 series cubre
// varios equipos en la misma tanda sin volverla ilimitada.
export const MAX_BACKFILL_SERIES = 100;
// Margen hacia adelante para aceptar un punto: el reloj del equipo NO se juzga
// (decisión del dueño del proyecto — ver specs/20260904-03), así que esto no es
// un control de desfase, solo el borde de lo que se puede guardar con sentido.
// Hacia atrás el borde es HISTORY_RETENTION_DAYS: más viejo que eso lo borraría
// RetentionJob en la próxima barrida, así que guardarlo sería inútil.
export const BACKFILL_MAX_FUTURE_SECONDS = 86400;
// Tope de turnos que un solo relleno recalcula solo. better-sqlite3 es
// SÍNCRONO: cada recálculo lee el histórico del turno y bloquea el event loop,
// o sea también el lazo de control (que se saltea ticks mientras tanto, ver el
// guard isRunning). 20 turnos son 10 días, de sobra para un corte de red real;
// un relleno más ancho que eso escribe igual TODO el dato y avisa por
// `warnings` que el resto se reprocesa a mano con
// POST /api/energy/shift-summaries/process.
export const MAX_BACKFILL_RECOMPUTED_SHIFTS = 20;

// --- Ventilador: variables de lectura del variador --------------------------
/** Los cinco tópicos de lectura con nombre propio en FanConfig y su variable
 *  VFD. settings-applier.ts los traduce a bindings; el resto del sistema los
 *  usa para saber qué NO es un parámetro adicional del operador. */
export const FAN_READ_TOPIC_VARIABLES: Array<[keyof FanConfig, string]> = [
  ['feedbackTopic', 'frequency'],
  // encendido/apagado REPORTADO por el propio ventilador — deliberadamente la
  // misma variable 'status' que usa la pestaña "VFD y energía" de /config.
  ['statusFeedbackTopic', 'status'],
  ['voltageTopic', 'voltage'],
  ['currentTopic', 'current'],
  ['powerTopic', 'power'],
  // palabra de estado CRUDA del variador — no es una medición: de ella sale
  // FanSnapshot.acceptsCommands (ver CONTROL_MODE_STATUS_BITS abajo).
  ['controlModeTopic', 'controlMode'],
];

/** Las cinco de arriba, como conjunto. Son las variables VFD que tienen campo
 *  o columna propia (FanSnapshot.measuredHz/measuredPowered,
 *  history_samples.voltage/current/power), así que:
 *  - un parámetro adicional NO puede llamarse como ninguna de ellas —
 *    comparten el keyspace `${fanId}:${variable}` de ControlService.vfdValues
 *    y la pisarían (settings.service.ts::sanitizeAdditionalTopics);
 *  - todo lo demás que aparezca en VfdSnapshot.configured ES un parámetro
 *    adicional (control-loop.ts::additionalValues,
 *    portal-state.builder.ts::additionalParametersOf).
 *  Derivado de FAN_READ_TOPIC_VARIABLES para que no haya dos listas que se
 *  desincronicen. */
export const FIXED_VFD_VARIABLES: ReadonlySet<string> = new Set(
  FAN_READ_TOPIC_VARIABLES.map(([, variable]) => variable),
);

/** Nombre en español de cada variable VFD fija, para las etiquetas
 *  user-facing (avisos de configuración, tópicos fuera de raíz, propuesta del
 *  descubrimiento). Vivía duplicado en out-of-root.ts y config-warnings.ts;
 *  está acá para que no haya una tercera copia que se desincronice. */
export const VFD_VARIABLE_LABEL: Record<string, string> = {
  frequency: 'lectura de frecuencia',
  status: 'lectura de estado',
  voltage: 'lectura de voltaje',
  current: 'lectura de corriente',
  power: 'lectura de potencia',
  controlMode: 'lectura de modo de control',
};

/** Bits de la palabra de estado del WEG CFW900 (S5.1.1, registro Modbus 680)
 *  que dicen QUIÉN manda al variador. Con cualquiera de los dos en 1 el
 *  variador ignora las órdenes del sistema: bit 2 = Local (teclado del
 *  variador), bit 12 = Remoto 2 (en Marcobre, bornes del tablero). Los dos en
 *  0 = Remoto 1 = el sistema. Fuente: manual Modbus del CFW900, S5.1.1. Lo
 *  usa ControlService.controlSource(). Dato de campo (bm-norte): selector del
 *  tablero = 4882 (bit 12); teclado = 788/790 (bit 2). */
export const CONTROL_MODE_STATUS_BITS = {
  local: 1 << 2,
  remote2: 1 << 12,
  fault: 1 << 15,
} as const;

/** Estado del WEG CFW900 (Net Id 6, tópico `estado`) que EXPLICA un motor
 *  parado — el motivo de la alerta `fan_stopped` (ControlService.stopReason()).
 *  0 Ready y 1 Run no explican nada. Fuente: docs/fan.md §4.1. */
export const VFD_STATUS_FAULT = 3;
/** Motivo de `fan_stopped` cuando lo apagó el propio sistema por ocupación:
 *  operación normal (pasa en cada turno), así que no va por WhatsApp. */
export const EMPTY_SHIFT_STOP_REASON = 'por el sistema (labor vacía)';
export const VFD_STOP_STATUS: Record<number, string> = {
  2: 'subtensión en el variador',
  5: 'seguridad activa en el variador (STO)',
  6: 'variador sin alimentación',
  7: 'variador deshabilitado',
};

/** Palabra de control del WEG CFW900 (S5.2.2, registro Modbus 682) que el
 *  sistema publica en FanConfig.controlTopic para tomar o soltar el mando.
 *  - systemRun (67): Remoto 1 + habilitado + marcha + sin paro rápido — el
 *    variador obedece la consigna del sistema y queda SIEMPRE en marcha (el
 *    encendido/apagado por ocupación no viaja por acá a propósito, decisión
 *    de campo 2026-10-19).
 *  - panel (82): Remoto 2 — devuelve el mando a los bornes del tablero.
 *  Ojo: un 1 o un 0 en ese registro DESHABILITA el variador (bit 1 =
 *  habilitación general); por eso controlTopic no puede ser statusTopic. */
export const CONTROL_WORD = { systemRun: 67, panel: 82 } as const;

/** Latido de la palabra de control: cada cuánto se REENVÍA la orden vigente
 *  (67 con la toma de mando, 82 sin ella) aunque el variador ya obedezca —
 *  un gateway reiniciado vuelve solo a 82, y uno con C4.1.1 (P220) en
 *  "siempre Remoto 2" nunca cede: se le insiste igual, sin costo (el gateway
 *  solo reescribe el registro). Un cambio del check sale en el acto. */
export const CONTROL_WORD_HEARTBEAT_SECONDS = 30;

// Tope de parámetros de lectura EXTRA por ventilador
// (FanConfig.additionalTopics, ver fan.types.ts). Acota el payload de
// /api/settings y la cantidad de suscripciones MQTT que un solo ventilador
// puede abrir; no hay ningún caso de campo cerca de este número.
export const MAX_ADDITIONAL_TOPICS_PER_FAN = 20;

// Tope del JSON de la ficha libre por ventilador (FanConfig.additionalInfo,
// ver fan.types.ts), medido sobre el JSON serializado. El contenido es opaco
// —el servidor no interpreta ninguna clave—, así que lo único que se puede
// acotar es el tamaño: esta fila se lee entera en cada getFans() y viaja en
// cada GET /api/settings. Es una ficha de equipo (marca, modelo, serie,
// notas), no un lugar donde guardar un manual ni una imagen en base64.
export const MAX_ADDITIONAL_INFO_BYTES = 8192;

// --- Explorador de tópicos por Socket.IO (ver socket/topic-explorer.ts) -----
// El operador que no sabe cómo se llama el tópico de su variador lo busca en
// una lista viva. NO abre ninguna suscripción MQTT: filtra el TopicStore, que
// ya tiene todo lo que entra por `<topicRoot>/#`.
// Cada cuánto se le reenvía el listado completo que coincide con su filtro.
export const TOPIC_EXPLORER_BATCH_SECONDS = 2;
// Sin señales de vida (keep-alive) del cliente durante este tiempo se cierra
// la suscripción. Puede ser generoso porque no cuesta nada del lado del
// broker — solo evita que un cliente que se fue mal deje un timer emitiendo.
export const TOPIC_EXPLORER_INACTIVITY_SECONDS = 300;
// Tope de tópicos por emisión. Importa porque el batch se manda ENTERO cada
// TOPIC_EXPLORER_BATCH_SECONDS: un '#' sobre un broker ruidoso mandaría las
// 2000 filas del TopicStore cada 2 s a cada cliente. Al recortar se avisa con
// `truncated: true`, misma convención que GET /api/history/samples.
export const TOPIC_EXPLORER_MAX_TOPICS = 500;

// --- Descubrimiento de tópicos por bocamina (ver mqtt/topic-discovery.ts) ---
// La convención de nombres que usan los equipos en campo, en UN solo lugar.
// Se aceptan los sinónimos en inglés porque el mismo variador puede venir
// configurado en cualquiera de los dos idiomas. Ojo con lo que NO está acá: el
// GRUPO (`fan-1`, `sensor-gas`, `ventilador-2`…) no se reconoce por su nombre
// sino por lo que cuelga de él, así que renombrar el equipo no rompe el
// descubrimiento.
/** Último segmento de un tópico de LECTURA -> campo de FanConfig. */
export const FAN_TOPIC_SEGMENT_READ: Record<string, keyof FanConfig> = {
  frecuencia: 'feedbackTopic',
  frequency: 'feedbackTopic',
  hz: 'feedbackTopic',
  marcha: 'statusFeedbackTopic',
  estado: 'statusFeedbackTopic',
  status: 'statusFeedbackTopic',
  run: 'statusFeedbackTopic',
  voltaje: 'voltageTopic',
  voltage: 'voltageTopic',
  tension: 'voltageTopic',
  corriente: 'currentTopic',
  current: 'currentTopic',
  potencia: 'powerTopic',
  power: 'powerTopic',
  palabra_estado: 'controlModeTopic',
  status_word: 'controlModeTopic',
};

/** El mismo segmento, pero con sufijo de escritura -> campo de FanConfig.
 *  `.../frecuencia` es lo que el ventilador REPORTA y `.../frecuencia/set` lo
 *  que el servidor le ORDENA: el sufijo es lo único que los distingue. */
export const FAN_TOPIC_SEGMENT_WRITE: Record<string, keyof FanConfig> = {
  frecuencia: 'commandTopic',
  frequency: 'commandTopic',
  hz: 'commandTopic',
  estado: 'statusTopic',
  marcha: 'statusTopic',
  status: 'statusTopic',
  control: 'controlTopic',
};

/** Últimos segmentos que marcan un tópico de ESCRITURA. */
export const FAN_WRITE_SUFFIXES: readonly string[] = ['set', 'cmd'];

/** Los dos tópicos de ESCRITURA del ventilador NO se pueden descubrir: son a
 *  los que el servidor publica, así que nadie los emite y nunca llegan al
 *  TopicStore (recién aparecen cuando el propio lazo empieza a publicarlos, ya
 *  configurado). Se DERIVAN por convención del grupo del equipo:
 *  `<raíz>/<bocamina>/<fan-N>/frecuencia/set` y `.../estado/set`. Es el único
 *  lugar donde el descubrimiento produce un tópico que no vio llegar — por eso
 *  la propuesta lo marca con `DiscoveredFan.writeTopicsDerived`. */
export const FAN_COMMAND_TOPIC_SUFFIX = 'frecuencia/set';
export const FAN_STATUS_TOPIC_SUFFIX = 'estado/set';
/** Palabra de control (gateway WEG CFW900). Solo se DEDUCE cuando el equipo
 *  publica su palabra de estado (controlModeTopic): sin ella el sistema no
 *  puede usarla (ver settings.service.ts::sanitizeFanInput). */
export const FAN_CONTROL_TOPIC_SUFFIX = 'control/set';

/** Hojas que identifican un semáforo/LED de estado (`.../led-1/color`). El
 *  tópico que se CONFIGURA en /config#led es el de ESCRITURA —el servidor
 *  publica ahí el color—, así que se deriva del de lectura pegándole `/set`,
 *  que es la convención del equipo. Igual que con el ventilador: el de
 *  escritura no se puede descubrir, nadie publica en él. */
export const STATUS_LIGHT_SEGMENTS: readonly string[] = ['color', 'semaforo', 'led'];
export const STATUS_LIGHT_WRITE_SUFFIX = 'set';

// Tope de tópicos que se miran bajo un prefijo. Mismo criterio que
// TOPIC_EXPLORER_MAX_TOPICS, pero acá el prefijo ya acota muchísimo: una
// bocamina real tiene decenas, no cientos.
export const TOPIC_DISCOVERY_MAX_TOPICS = 500;

// Ventana de frescura del descubrimiento. El TopicStore guarda el último
// mensaje de cada tópico PARA SIEMPRE (no vence), así que sin esta reja la
// propuesta seguía ofreciendo un sensor de gas que dejó de publicar hace 20
// minutos — indistinguible de uno vivo, y es justo el equipo que NO hay que
// configurar. Solo entra a la propuesta lo que publicó dentro de esta ventana.
// Ojo con el caso que queda deliberadamente afuera de la reja: los tópicos de
// ESCRITURA (ver FAN_COMMAND_TOPIC_SUFFIX) no son señal del equipo sino de
// nuestras propias publicaciones, que salen cada FAN_HEARTBEAT_SECONDS — con
// la reja puesta también sobre ellos, un ventilador ya configurado con un
// tópico fuera de convención alternaría cada minuto entre el real y el
// derivado.
export const TOPIC_DISCOVERY_MAX_AGE_SECONDS = 60;

// Tope de subida de archivos (POST /api/files/upload, ver src/config/upload.ts).
// Subió de 10MB a 100MB para permitir modelos 3D (.glb del mapa de la mina).
// Ojo: escribe en la SD de la Raspberry Pi y todavía no hay endpoint de borrado
// ni de listado, así que public/uploads crece sin tope.
export const MAX_UPLOAD_SIZE_MB = 100;

// --- Padrón / descubrimiento de fotochecks (ver specs/0001-personal-padron.md) ---
// cuánto se conserva una baliza en la lista "Vincular en vivo" sin volver a verse
export const DETECTION_TTL_SECONDS = 120;
// cuánto sigue una baliza colgando de UN escáner concreto en la cadena de
// /pruebas. Mucho más corto que DETECTION_TTL_SECONDS a propósito: aquello
// responde a "se vio hace poco, se puede vincular"; esto a "este lector la
// está viendo ahora", y debe vaciarse en cuanto la baliza se aleja.
export const SCANNER_SIGHTING_TTL_SECONDS = 30;
// NOTA: aquí vivían BADGE_EVENT_MAX_AGE_S y MIN_VALID_EPOCH, el descarte de
// eventos por antigüedad del `ts` del lector. Se eliminaron el 2026-08-10: la
// hora de un evento es la de llegada al servidor y el `ts` del lector ya no
// decide nada (ver specs/20260810-01-hora-del-evento-es-la-de-llegada.md).

// --- Dirección de tránsito por orden de escáneres (ver specs/20260807-02…) ---
// orden que se le asigna a un escáner exterior sin `sortOrder` explícito
// (fallback heredado 1+1, mayor que cualquier orden real asignable desde
// /config#escaneres) — así toda comparación ordinal lo trata como "el más
// afuera" sin que el operador tenga que configurar nada.
export const OUTERMOST_FALLBACK_ORDER = 9999;
// una baliza DENTRO que lleva este tiempo sin que la vea ningún escáner se
// marca como silenciosa en el tablero, con el botón para sacarla a mano. NO
// se la saca sola: si sigue abajo con la baliza muerta, darla por fuera
// ventilaría de menos y un rescate creería que no hay nadie. Solo pinta el
// aviso — ninguna decisión de control cuelga de este umbral.
export const PRESENCE_SILENT_SECONDS = 4 * 3600;

// --- Simulación de escenario (/simulacion) ----------------------------------
// Altas de prueba para poder ejercitar el sistema sin hardware: N trabajadores
// con su fotocheck ya vinculado, y una cadena de lectores por bocamina. NO
// publica nada al broker ni participa de ninguna decisión de ventilación — el
// motor que hace ENTRAR a esta gente publicando MQTT es un spec aparte, y va a
// publicar justamente con la MAC/`topic_base`/`segment` que se crean acá.
//
// Lo simulado se reconoce por PREFIJO, no por una columna nueva: `workers` no
// tiene de dónde colgar un flag (`source` es 'manual'|'csv'|'api' y agregar un
// valor obligaría a tocar el tipo y el padrón entero), y el prefijo alcanza
// para listarlo y darlo de baja.
export const SIM_WORKER_DNI_PREFIX = 'SIM-';
export const SIM_WORKER_COMPANY = 'Simulación';
export const SIM_WORKER_ROLE = 'Operario';
// El `cb:20:25` es el prefijo real de las balizas de campo (ver
// examples/bocamina.md); el `f0` marca que es simulada. Los 4 hex finales son
// el índice, así que la MAC es determinista: volver a crear la misma cantidad
// no genera fotochecks nuevos.
export const SIM_BADGE_MAC_PREFIX = 'cb2025f0';
export const SIM_BADGE_CODE_BASE = 900000;
// Tope de una tanda. better-sqlite3 es SÍNCRONO y el alta corre en el mismo
// hilo que el lazo de control: sin tope, un 100000 de dedo lo bloquea.
export const MAX_SIM_WORKERS = 500;

// MAC de lector simulado: 3 octetos de prefijo (02 = administrada localmente,
// 53 4d = "SM") + 4 hex de bocamina + 2 hex de posición en la cadena.
export const SIM_SCANNER_MAC_PREFIX = '02534d';
export const SIM_SCANNER_FW = 'sim';
/** La cadena que se crea por bocamina, en el orden que exige
 *  scanners.service.ts::validateChains() para N=3: el orden 1 es `interior`
 *  (confirma el ingreso), el N es `exterior` (confirma la salida) y el resto
 *  `middle`. El índice del arreglo + 1 ES el `sort_order`. */
export const SIM_SCANNER_CHAIN: ReadonlyArray<{
  segment: string;
  zone: ScannerZone;
  label: string;
}> = [
  { segment: 'int', zone: 'interior', label: 'simulado · fondo' },
  { segment: 'med', zone: 'middle', label: 'simulado · paso intermedio' },
  { segment: 'ext', zone: 'exterior', label: 'simulado · boca del socavón' },
];

// --- Motor de simulación (/simulacion, ver modules/simulation/simulation-
// engine.ts) ----------------------------------------------------------------
// Hace CAMINAR a los fotochecks simulados publicando MQTT de verdad en los
// tópicos de los lectores simulados de arriba: es la mitad que el alta de
// escenario dejó pendiente a propósito. No inventa ningún contrato — publica
// el payload del firmware `wapsi/lector` y entra por la misma suscripción
// `<raíz>/#` que el hardware real, así que ejercita dispatcher, contratos,
// TopicRegistry y ControlService sin ninguna vía de prueba paralela.
export const SIM_ENGINE_TICK_SECONDS = 1;
// Latido /status de cada lector simulado. DEBE ser bastante menor que
// READER_OFFLINE_SECONDS (90): sin él, el primer evento de badge mete al
// lector en ControlService.readerLiveness y 90 s después la bocamina entra en
// fail-safe con el motor todavía corriendo.
export const SIM_ENGINE_STATUS_PERIOD_SECONDS = 30;
// NOTA: acá vivía SIM_ENGINE_HEARTBEAT_SECONDS, un latido `presente` cada 30 s
// en el escáner interior mientras la persona estaba adentro. Se eliminó: en la
// mina real quien entra se va al fondo y ningún lector del portal lo alcanza,
// así que ese latido modelaba a alguien parado al lado del lector una hora.
// Ahora el terminal publica su `desaparece` a los `hopSeconds` y después hay
// silencio — ver simulation-walk.ts. El silencio es seguro: el motor de
// presencia no saca a nadie por tiempo ("el silencio nunca es salida").
// Tope de publicaciones por tick. Con 500 fotochecks el reparto inicial y los
// latidos pueden coincidir en el mismo segundo; lo que sobra va al tick
// siguiente en vez de golpear el hilo del lazo de control de una.
export const SIM_ENGINE_MAX_PUBLISH_PER_TICK = 40;
// Espera entre la última salida del vaciado ordenado y el olvido de los
// lectores en readerLiveness. Sin esta gracia, esas mismas salidas —que dan la
// vuelta por el broker— vuelven a poblar el mapa justo después de limpiarlo.
export const SIM_ENGINE_DRAIN_GRACE_SECONDS = 3;
// Un salto de la cadena DEBE tardar menos que directionCommitSeconds (60) o
// el sweep() confirma por silencio ('timeout', corrected: true) antes de que
// la baliza llegue al escáner terminal: el escenario seguiría "funcionando",
// pero cada ingreso quedaría registrado como corregido y no probaría el
// camino que se quiere probar.
export const MAX_SIM_HOP_SECONDS = 50;
export const MAX_SIM_STAY_MINUTES = 720;
export const SIM_ENGINE_DEFAULTS: SimulationEngineConfig = {
  workers: 0,
  stayMinutes: [30, 50, 70],
  outsideMinutes: 5,
  hopSeconds: 20,
  arrivalSpreadMinutes: 10,
  portalSwitchEvery: 3,
  portalIds: [],
};

// Una estadía (salida con duración) más larga que esto casi seguro es una
// falla — un ingreso que nunca vio su salida y se cerró días después — no un
// turno real. /api/access/summary la deja FUERA de total/promedio/máximo y la
// cuenta aparte (`suspectExits`); /api/access/movements la marca `suspect`.
export const ACCESS_SUSPECT_STAY_SECONDS = 24 * 3600;
